import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:rtl:[transform:scaleX(-1)] touch-manipulation",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90 ",
        destructive:
          "bg-destructive text-destructive-foreground shadow hover:bg-destructive/90 ",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground dark:border-gray-600 dark:hover:border-gray-400",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 dark:bg-gray-800 dark:text-gray-100 dark:shadow-gray-500/20",
        ghost: "hover:bg-accent hover:text-accent-foreground ",
        link: "text-primary underline-offset-4 hover:underline ",
        success:
          "bg-green-50 dark:bg-green-900/30 text-white shadow hover:bg-green-600 dark:shadow-green-500/25",
        warning:
          "bg-yellow-50 dark:bg-yellow-900/30 text-white shadow hover:bg-yellow-600 dark:shadow-yellow-500/25",
        info:
          "bg-blue-50 dark:bg-blue-900/30 text-white shadow hover:bg-blue-600 dark:shadow-blue-500/25",
        // Glow variants for dark mode
        glow:
          "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg hover:shadow-xl dark:shadow-purple-500/25 dark:hover:shadow-purple-500/40",
        glowGreen:
          "bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg hover:shadow-xl dark:shadow-green-500/25 dark:hover:shadow-green-500/40",
        glowBlue:
          "bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg hover:shadow-xl dark:shadow-cyan-500/25 dark:hover:shadow-cyan-500/40",
        glowOrange:
          "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg hover:shadow-xl dark:shadow-orange-500/25 dark:hover:shadow-orange-500/40",
        glowRed:
          "bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg hover:shadow-xl dark:shadow-red-500/25 dark:hover:shadow-red-500/40",
        glowTeal:
          "bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg hover:shadow-xl dark:shadow-teal-500/25 dark:hover:shadow-teal-500/40",
      },
      size: {
        xs: "h-7 px-2 text-xs min-h-[36px] min-w-[36px]",
        sm: "h-8 px-3 text-xs min-h-[40px] min-w-[40px]",
        default: "h-9 px-4 text-sm min-h-[44px] min-w-[44px]",
        lg: "h-10 px-6 text-base min-h-[48px] min-w-[48px]",
        xl: "h-12 px-8 text-lg min-h-[52px] min-w-[52px]",
        icon: "h-9 w-9 min-h-[44px] min-w-[44px]",
        "icon-sm": "h-8 w-8 min-h-[40px] min-w-[40px]",
        "icon-lg": "h-10 w-10 min-h-[48px] min-w-[48px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type = "button", ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        type={type}
        className={cn(
          buttonVariants({ variant, size, className }),
          // Arabic RTL touch optimizations - using conditional classes
          size === "default" && "rtl:arabic-touch-target rtl:min-h-[44px] rtl:min-w-[44px]",
          size === "lg" && "rtl:min-h-[48px] rtl:min-w-[48px]",
          size === "xl" && "rtl:min-h-[52px] rtl:min-w-[52px]",
          size === "sm" && "rtl:min-h-[40px] rtl:min-w-[40px]",
          size === "icon" && "rtl:min-h-[44px] rtl:min-w-[44px]",
          size === "icon-lg" && "rtl:min-h-[48px] rtl:min-w-[48px]",
          size === "icon-sm" && "rtl:min-h-[40px] rtl:min-w-[40px]"
        )}
        suppressHydrationWarning
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }